import { z } from 'zod';

/**
 * Aisles of the family's own shopping list (Release 1.20), in the order a typical German
 * supermarket is walked: fruit and vegetables at the door, drinks and the household aisle last.
 * The tiles are grouped in this order; "other" collects what we do not know.
 */
export const AISLES = [
  'produce',
  'bakery',
  'dairy',
  'meat',
  'pantry',
  'baking',
  'frozen',
  'snacks',
  'drinks',
  'household',
  'other',
] as const;
export const Aisle = z.enum(AISLES);
export type Aisle = z.infer<typeof Aisle>;

/**
 * Word endings per aisle, German and English, lower case. A name matches when one of its words
 * ends with a keyword – so compounds land right ("Cherrytomaten" → tomaten, "Vollmilch" → milch).
 * The longest matching keyword wins, which lets specific words beat general ones
 * ("Erdnussbutter" is pantry although it ends in "butter").
 */
const KEYWORDS: Record<Exclude<Aisle, 'other'>, string[]> = {
  produce: [
    'apfel',
    'äpfel',
    'apple',
    'birne',
    'pear',
    'banane',
    'banana',
    'orange',
    'mandarine',
    'clementine',
    'zitrone',
    'lemon',
    'limette',
    'lime',
    'traube',
    'grape',
    'erdbeere',
    'strawberr',
    'himbeere',
    'raspberr',
    'heidelbeere',
    'blaubeere',
    'blueberr',
    'beere',
    'berry',
    'kiwi',
    'mango',
    'ananas',
    'pineapple',
    'melone',
    'melon',
    'pfirsich',
    'peach',
    'nektarine',
    'pflaume',
    'plum',
    'kirsche',
    'cherry',
    'cherries',
    'avocado',
    'tomate',
    'tomato',
    'gurke',
    'cucumber',
    'paprika',
    'pepper',
    'salat',
    'lettuce',
    'rucola',
    'rocket',
    'spinat',
    'spinach',
    'kartoffel',
    'potato',
    'süßkartoffel',
    'zwiebel',
    'onion',
    'lauchzwiebel',
    'knoblauch',
    'garlic',
    'ingwer',
    'ginger',
    'möhre',
    'karotte',
    'carrot',
    'brokkoli',
    'broccoli',
    'blumenkohl',
    'cauliflower',
    'zucchini',
    'courgette',
    'aubergine',
    'kürbis',
    'pumpkin',
    'squash',
    'lauch',
    'porree',
    'leek',
    'sellerie',
    'celery',
    'pilz',
    'champignon',
    'mushroom',
    'radieschen',
    'radish',
    'kohl',
    'cabbage',
    'kohlrabi',
    'fenchel',
    'fennel',
    'mais',
    'spargel',
    'asparagus',
    'bohne',
    'erbse',
    'petersilie',
    'parsley',
    'basilikum',
    'basil',
    'schnittlauch',
    'chives',
    'koriander',
    'coriander',
    'dill',
    'minze',
    'mint',
    'kräuter',
    'herbs',
    'obst',
    'fruit',
    'gemüse',
    'veg',
    'vegetable',
    'rhabarber',
    'feige',
    'fig',
    'granatapfel',
  ],
  bakery: [
    'brot',
    'bread',
    'brötchen',
    'semmel',
    'roll',
    'toast',
    'baguette',
    'croissant',
    'brezel',
    'breze',
    'pretzel',
    'bagel',
    'wrap',
    'tortilla',
    'fladenbrot',
    'pitta',
    'pita',
    'kuchen',
    'cake',
    'zwieback',
    'knäckebrot',
    'muffin',
    'crumpet',
    'hefezopf',
  ],
  dairy: [
    'milch',
    'milk',
    'joghurt',
    'jogurt',
    'yoghurt',
    'yogurt',
    'quark',
    'skyr',
    'sahne',
    'cream',
    'schmand',
    'crème fraîche',
    'creme fraiche',
    'butter',
    'margarine',
    'käse',
    'cheese',
    'feta',
    'mozzarella',
    'parmesan',
    'gouda',
    'cheddar',
    'frischkäse',
    'halloumi',
    'ricotta',
    'mascarpone',
    'ei',
    'eier',
    'egg',
    'aufschnitt',
    'schinken',
    'ham',
    'salami',
    'wurst',
    'würstchen',
    'sausage',
    'tofu',
    'hummus',
    'pudding',
    'kefir',
    'buttermilch',
    'oatly',
    'hafermilch',
    'sojamilch',
    'pesto',
    'gnocchi',
    'maultaschen',
    'blätterteig',
    'pizzateig',
  ],
  meat: [
    'fleisch',
    'meat',
    'hack',
    'hackfleisch',
    'mince',
    'hähnchen',
    'huhn',
    'chicken',
    'pute',
    'turkey',
    'rind',
    'beef',
    'schwein',
    'pork',
    'steak',
    'schnitzel',
    'bratwurst',
    'speck',
    'bacon',
    'lamm',
    'lamb',
    'fisch',
    'fish',
    'lachs',
    'salmon',
    'thunfischsteak',
    'garnele',
    'prawn',
    'shrimp',
    'forelle',
    'kabeljau',
    'cod',
    'filet',
    'gyros',
    'frikadelle',
  ],
  pantry: [
    'nudel',
    'pasta',
    'spaghetti',
    'penne',
    'fusilli',
    'lasagne',
    'reis',
    'rice',
    'couscous',
    'bulgur',
    'quinoa',
    'linse',
    'lentil',
    'kichererbse',
    'chickpea',
    'dose',
    'tin',
    'konserve',
    'passata',
    'passierte tomaten',
    'tomatenmark',
    'tomato puree',
    'ketchup',
    'senf',
    'mustard',
    'mayo',
    'mayonnaise',
    'öl',
    'oil',
    'olivenöl',
    'essig',
    'vinegar',
    'sojasoße',
    'sojasauce',
    'soy sauce',
    'brühe',
    'stock',
    'soße',
    'sauce',
    'müsli',
    'muesli',
    'haferflocken',
    'oats',
    'cornflakes',
    'cereal',
    'marmelade',
    'konfitüre',
    'jam',
    'honig',
    'honey',
    'nutella',
    'erdnussbutter',
    'kaffeebohne',
    'peanut butter',
    'thunfisch',
    'tuna',
    'kaffee',
    'coffee',
    'tee',
    'tea',
    'kakao',
    'cocoa',
    'nuss',
    'nüsse',
    'nut',
    'mandel',
    'almond',
    'rosine',
    'raisin',
    'kokosmilch',
    'coconut milk',
    'brotaufstrich',
    'suppe',
    'soup',
    'kidneybohne',
    'baked beans',
  ],
  baking: [
    'mehl',
    'flour',
    'zucker',
    'sugar',
    'backpulver',
    'baking powder',
    'natron',
    'hefe',
    'yeast',
    'vanille',
    'vanilla',
    'puderzucker',
    'speisestärke',
    'stärke',
    'cornflour',
    'salz',
    'salt',
    'pfeffer',
    'gewürz',
    'spice',
    'zimt',
    'cinnamon',
    'oregano',
    'curry',
    'kurkuma',
    'paprikapulver',
    'muskat',
    'nutmeg',
    'streusel',
    'kuvertüre',
    'schokotropfen',
    'gelatine',
  ],
  frozen: [
    'tiefkühl',
    'tk-',
    'frozen',
    'eis',
    'ice cream',
    'pizza',
    'pommes',

    'fischstäbchen',
    'fish fingers',
    'rahmspinat',
    'erbsen tk',
    'eiswürfel',
    'ice',
  ],
  snacks: [
    'schokolade',
    'schoko',
    'chocolate',
    'chips',
    'crisps',
    'keks',
    'kekse',
    'biscuit',
    'cookie',
    'gummibärchen',
    'gummi',
    'sweets',
    'bonbon',
    'süßigkeit',
    'popcorn',
    'cracker',
    'salzstange',
    'flips',
    'riegel',
    'bar',
    'lakritz',
    'liquorice',
    'waffel',
    'wafer',
    'müsliriegel',
  ],
  drinks: [
    'wasser',
    'water',
    'sprudel',
    'saft',
    'juice',
    'schorle',
    'limo',
    'limonade',
    'lemonade',
    'cola',
    'fanta',
    'sprite',
    'bier',
    'beer',
    'wein',
    'wine',
    'sekt',
    'prosecco',
    'sirup',
    'squash drink',
    'eistee',
    'iced tea',
    'smoothie',
  ],
  household: [
    'klopapier',
    'toilettenpapier',
    'toilet roll',
    'toilet paper',
    'küchenrolle',
    'kitchen roll',
    'taschentuch',
    'taschentücher',
    'tissues',
    'spülmittel',
    'washing-up',
    'spülmaschinentabs',
    'tabs',
    'waschmittel',
    'detergent',
    'weichspüler',
    'zahnpasta',
    'toothpaste',
    'zahnbürste',
    'toothbrush',
    'shampoo',
    'duschgel',
    'shower gel',
    'seife',
    'soap',
    'deo',
    'deodorant',
    'windel',
    'nappy',
    'nappies',
    'diaper',
    'feuchttücher',
    'wipes',
    'müllbeutel',
    'bin bag',
    'alufolie',
    'foil',
    'frischhaltefolie',
    'cling film',
    'backpapier',
    'baking paper',
    'schwamm',
    'sponge',
    'batterie',
    'battery',
    'creme',
    'sonnencreme',
    'sun cream',
    'pflaster',
    'plaster',
    'binden',
    'tampon',
    'rasierer',
    'razor',
    'putzmittel',
    'reiniger',
    'cleaner',
  ],
};

/** Plural and case endings we try to drop ("Tomaten" → tomate, "Eggs" → egg). */
const ENDINGS = ['en', 'n', 'e', 's', 'es'];

function candidates(word: string): string[] {
  const out = [word];
  for (const e of ENDINGS) {
    if (word.length > e.length + 2 && word.endsWith(e)) out.push(word.slice(0, -e.length));
  }
  return out;
}

/**
 * The aisle for an article name, or "other". Pure and fast (runs on every keystroke-free add);
 * the family can always move an item, and that choice stays with the item.
 */
export function aisleOf(name: string): Aisle {
  const text = name.toLowerCase().trim();
  if (!text) return 'other';
  // Score: a phrase keyword beats everything, then the last word (the head noun: "Orange
  // juice" is juice), then the longest keyword.
  let best: { aisle: Aisle; score: number } | undefined;
  const consider = (aisle: Aisle, score: number) => {
    if (!best || score > best.score) best = { aisle, score };
  };
  const words = text.split(/[\s,/()+&-]+/).filter(Boolean);
  for (const [aisle, keys] of Object.entries(KEYWORDS) as [Aisle, string[]][]) {
    for (const k of keys) {
      if (k.includes(' ')) {
        // multi-word keywords ("peanut butter") match on the whole name
        if (text.includes(k)) consider(aisle, 200 + k.length);
        continue;
      }
      words.forEach((w, i) => {
        // very short keywords ("ei", "tin") only as a whole word – "Brei" is no egg
        const hit = candidates(w).some((c) => (k.length <= 3 ? c === k : c.endsWith(k)));
        if (hit) consider(aisle, (i === words.length - 1 ? 100 : 0) + k.length);
      });
    }
  }
  return best?.aisle ?? 'other';
}

/** Same article? Case, spaces and simple plurals do not matter ("Tomaten" = "tomate"). */
export function sameArticle(a: string, b: string): boolean {
  const norm = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');
  const x = norm(a);
  const y = norm(b);
  if (x === y) return true;
  const cx = candidates(x);
  return candidates(y).some((c) => cx.includes(c));
}

const UNIT =
  '(?:g|gr|kg|ml|l|liter|litre|litres|liters|stück|stk|st|x|packung|packungen|pck|pkg|pack|packs|dose|dosen|tin|tins|can|cans|flasche|flaschen|bottle|bottles|bund|bunch|becher|glas|gläser|jar|jars|tüte|tüten|bag|bags|kiste|kisten|crate)';
// String.raw keeps the backslashes – a plain template literal turns "\d" into "d"
const AMOUNT = String.raw`(\d+(?:[.,]\d+)?\s*${UNIT}?\.?|ein(?:e|en)?\s+${UNIT}|a\s+${UNIT})`;
const LEADING = new RegExp(String.raw`^${AMOUNT}\s+(.+)$`, 'i');
const TRAILING = new RegExp(String.raw`^(.+?)\s+${AMOUNT}$`, 'i');

/**
 * One field for what is missing: "2 Liter Milch", "Milch 2 l", "3 Paprika" become name + amount,
 * so nobody has to fill two fields in the shop. Anything else stays the name as typed.
 */
export function parseEntry(input: string): { name: string; spec: string } {
  const text = input.trim().replace(/\s+/g, ' ');
  const lead = LEADING.exec(text);
  if (lead) return { name: lead[2]!.trim(), spec: lead[1]!.trim() };
  const trail = TRAILING.exec(text);
  if (trail) return { name: trail[1]!.trim(), spec: trail[2]!.trim() };
  return { name: text, spec: '' };
}
